import { useState, useEffect, useCallback } from 'react';
import { BACKEND_API } from '../constants/backendApiEndpoints';

interface UseFeedbackOptions {
  jobId: number | null;
  pipelineType: string;
  jobStatus: string;
  delayMs?: number; // Delay before showing modal (default: 15000ms)
}

interface UseFeedbackReturn {
  showFeedbackModal: boolean;
  setShowFeedbackModal: (show: boolean) => void;
  hasFeedback: boolean;
  isCheckingFeedback: boolean;
}

/**
 * Hook to manage feedback modal display logic.
 * 
 * - Shows modal after delay when viewing completed job results
 * - Checks if feedback already exists (won't show again if submitted)
 * - Skip only lasts for current session
 */
export const useFeedback = ({
  jobId,
  pipelineType,
  jobStatus,
  delayMs = 15000,
}: UseFeedbackOptions): UseFeedbackReturn => {
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [hasFeedback, setHasFeedback] = useState(false);
  const [isCheckingFeedback, setIsCheckingFeedback] = useState(false);
  const [hasChecked, setHasChecked] = useState(false);

  // Check if feedback exists for this job
  const checkFeedback = useCallback(async () => {
    const token = localStorage.getItem('access_token');
    if (!jobId || !token) return;

    setIsCheckingFeedback(true);
    try {
      const response = await fetch(
        BACKEND_API.FEEDBACK.CHECK(jobId),
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setHasFeedback(data.has_feedback);
      }
    } catch (error) {
      console.error('Error checking feedback:', error);
    } finally {
      setIsCheckingFeedback(false);
      setHasChecked(true);
    }
  }, [jobId]);

  // Check feedback when job ID changes
  useEffect(() => {
    if (jobId) {
      setHasChecked(false);
      checkFeedback();
    }
  }, [jobId, checkFeedback]);

  // Show modal after delay if job is completed and no feedback exists
  useEffect(() => {
    if (!jobId || !hasChecked || isCheckingFeedback) return;
    
    // Only show for completed/successful jobs
    const isCompleted = ['completed', 'successful', 'success'].includes(
      jobStatus?.toLowerCase() || ''
    );
    
    if (!isCompleted || hasFeedback) return;

    // Set timer to show modal
    const timer = setTimeout(() => {
      setShowFeedbackModal(true);
    }, delayMs);

    return () => clearTimeout(timer);
  }, [jobId, hasChecked, isCheckingFeedback, jobStatus, hasFeedback, delayMs]);

  return {
    showFeedbackModal,
    setShowFeedbackModal,
    hasFeedback,
    isCheckingFeedback,
  };
};

export default useFeedback;
